/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {WizardService} from '../../shared/wizard/wizard.service';
import { HttpClient } from '@angular/common/http';
import {SpoCreateNewService} from '../../api/ms-specialorders/specialorder/spo-create-new.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {SpecialOrderMaterialCommandModel} from '../../api/ms-specialorders/contracts/special-order-material-command.model';
import {StepHeaderModel} from './step-header/step-header.model';
import {SpecialOrderMaterialDetailModel} from '../../api/ms-specialorders/contracts/special-order-material-detail.model';
import {SpecialOrderCommandModel} from '../../api/ms-specialorders/contracts/special-order-command.model';
import {StepMaterialListModel} from './step-material-list/step-material-list.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {finalize} from 'rxjs/operators';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {SpoHeaderDataModel} from '../models/spo-header-data.model';
import {UnsavedChangesService} from '../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../shared/services/replayable-router-events.service';
import {SpecialOrderBaseModel} from '../../api/ms-specialorders/contracts/special-order-base.model';
import {MultiStatusModel} from '../../api/generic/contracts/multi-status.model';
import {CreateAndSubmitActionsEnum} from '../../api/generic/contracts/create-and-submit-actions.enum';
import {SpoActionsUtilService} from '../services/spo-actions-util/spo-actions-util.service';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

@Component({
    selector: 'app-spo-create-new',
    templateUrl: './spo-create-new.component.html',
    styleUrls: ['./spo-create-new.component.css'],
    providers: [WizardService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpoCreateNewComponent implements OnInit, OnDestroy {

    public formSubmitted: boolean;
    public isExited = false;
    /*
     * TODO : Create a shared file w/ const values across the application
     */
    public MODAL_TITLE = 'SPO yet';
    private subscriptions = new Subscription();
    private submitModalWidth = 620;

    constructor(
            private router: Router,
            private wizardService: WizardService,
            private http: HttpClient,
            private spoCreateService: SpoCreateNewService,
            private modalService: NgbModal,
            private toast: ToastWrapperService,
            private spinnerService: SpinnerService,
            private unsavedChangesService: UnsavedChangesService,
            private replay: ReplayableRouterEventsService,
            private spoActionsUtilService: SpoActionsUtilService
    ) {
    }

    ngOnInit(): void {
        this.formSubmitted = false;
        this.unsavedChangesService.originalValue = {} as SpoHeaderDataModel;
        this.subscriptions.add(
                this.replay.onGuardsCheckStart.subscribe(() => {
                    this.updateCurrentState();
                })
        );
    }

    ngOnDestroy(): void {
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, SpoCreateNewComponent);
        this.subscriptions.unsubscribe();
    }

    sendSpo(): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        confirmModalInstance.initModal({
            titleText: 'Confirm',
            subtitleText: 'Are you sure you want to send the SPO?',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            primaryButtonText: 'Send',
            secondaryButtonText: 'Cancel',
            width: this.submitModalWidth,
            manualChecks: {
                title: `<strong>Be aware that:</strong>`,
                inputs: [
                    {
                        checked: false,
                        message: 'This action will send the SPO to BMW and the process will be started'
                    },
                    {
                        checked: false,
                        message: 'Edit and Cancel options for open SPO are only available for Materials with "submitted" status'
                    }
                ]
            }
        } as ConfirmModal)
                .confirmEvent
                .subscribe(() => {
                    this.isExited = true;
                    this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, SpoCreateNewComponent);
                    this.subscriptions.add(
                            this.spoCreateService.createAndSendNewSpo(
                                    this.createSpo())
                                    .pipe(finalize(() => {
                                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, SpoCreateNewComponent);
                                    }))
                                    .subscribe(
                                            (response) => {
                                                if (response.status === 207) {
                                                    const multiStatus = response.body as MultiStatusModel;
                                                    const createAction = multiStatus.actions
                                                            .find((status) => status.action === CreateAndSubmitActionsEnum.CREATE);
                                                    const submitAction = multiStatus.actions
                                                            .filter((status) => status.action === CreateAndSubmitActionsEnum.SUBMIT
                                                                    && status.error == null);

                                                    const spoNumber = createAction.identifier;
                                                    const materialCount = (this.wizardService.steps.get(
                                                            2) as StepMaterialListModel).data.length;
                                                    const successCount = submitAction.length;
                                                    const errorMessage = multiStatus.actions.find((s) => s.error != null).error.message;

                                                    this.toast.error(
                                                            this.spoActionsUtilService.getMultiStatusToastErrorMessage(successCount,
                                                                    materialCount, spoNumber, errorMessage, 'submitted'), 'Error');
                                                } else {
                                                    const specialOrderBaseModel = response.body as SpecialOrderBaseModel;
                                                    this.toast.success(
                                                            `SPO No.
                                            <span class="toast-new-spo-number">${specialOrderBaseModel.number}</span>
                                            <strong>sent</strong> successfully`,
                                                            'Success');
                                                }
                                                this.router.navigate(['/', 'spo']);
                                            })
                    );
                });
    }

    closeWizard(): void {
        if (!this.wizardService.isEmpty && this.wizardService.isTouched) {
            const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
                centered: true
            }).componentInstance;
            confirmModalInstance.initModal({
                titleText: 'Confirm',
                subtitleText: `You haven't finished your SPO yet! Are you sure you want to leave and remove all the unsaved information?`,
                alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
                primaryButtonText: 'Stay on this page',
                secondaryButtonText: 'Leave this page'
            } as ConfirmModal)
                    .secondaryEvent
                    .subscribe(() => {
                        this.isExited = true;
                        this.router.navigate(['/', 'spo']);
                    });
        } else {
            this.isExited = true;
            this.router.navigate(['/', 'spo']);
        }
    }

    saveSpo(): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        confirmModalInstance.initModal({
            titleText: 'Confirm',
            subtitleText: 'Do you want to save the new SPO as draft?',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            primaryButtonText: 'Save',
            secondaryButtonText: 'Cancel'
        } as ConfirmModal)
                .confirmEvent
                .subscribe(
                        () => {
                            this.isExited = true;
                            this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, SpoCreateNewComponent);
                            this.subscriptions.add(
                                    this.spoCreateService.createNewSpo(
                                            this.createSpo())
                                            .pipe(finalize(() => {
                                                this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, SpoCreateNewComponent);
                                            }))
                                            .subscribe(
                                                    (data) => {
                                                        this.toast.success(
                                                                `SPO No.
                                            <span class="toast-new-spo-number">${data.number}</span>
                                            <strong>saved</strong> successfully`,
                                                                'Success');
                                                        this.router.navigate(['/', 'spo']);
                                                    })
                            );
                        }
                );
    }

    createSpo(): SpecialOrderCommandModel {
        const headerStep = this.wizardService.steps.get(0) as StepHeaderModel;
        const filteredMaterialList = this.wizardService.steps.get(2) as StepMaterialListModel;
        const spoMaterials = filteredMaterialList.data.map((x: SpecialOrderMaterialDetailModel) => ({
            number: x.material.number,
            lotNumber: x.lotNumber,
            mcdrNumber: x.mcdrNumber,
            igefNumber: x.igefNumber,
            colourCode: x.colourCode,
            modelType: x.modelType,
            orderedQuantity: x.orderedQuantity
        } as SpecialOrderMaterialCommandModel));

        return {
            partnerCode: headerStep.data.partner.code,
            shipToPartyCode: headerStep.data.shipToParty.code,
            supplyingPackingPlantCode: headerStep.data.supplyingPackingPlant.code,
            reasonCode: headerStep.data.reason ? headerStep.data.reason.code : null,
            requestedDate: headerStep.data.requestedDate,
            transportModeCode: headerStep.data.transportMode ? headerStep.data.transportMode.code : null,
            transportServiceCode: headerStep.data.transportService ? headerStep.data.transportService.code : null,
            justification: headerStep.data.justification && headerStep.data.justification.length > 0
                    ? toBase64Utf16LittleEndian(headerStep.data.justification)
                    : null,
            specialOrderMaterials: spoMaterials
        };
    }

    private updateCurrentState(): void {
        const headerStep = this.wizardService.steps.get(0) as StepHeaderModel;
        if (headerStep) {
            this.unsavedChangesService.currentValue = {...headerStep.data};
        }
    }
}
